import dayjs from "dayjs";

/**
 * Validates a required text field.
 * Accepts string iff it contains at least 1 non-whitespace character
 * and length is between minLength and maxLength (inclusive).
 *
 * @param {string} value - The string to validate
 * @param {number} minLength - Minimum allowed length (inclusive)
 * @param {number} maxLength - Maximum allowed length (inclusive)
 * @returns {boolean} true if valid, false otherwise
 */
export function validateRequiredText(value, minLength, maxLength) {
  if (typeof value !== "string") return false;
  if (value.trim().length === 0) return false;
  if (value.length < minLength || value.length > maxLength) return false;
  return true;
}

/**
 * Validates a phone number.
 * Accepts string iff it contains only digits (0-9), parentheses, hyphens,
 * spaces, and an optional leading plus sign, AND length <= 20 characters.
 * Empty/null values return true (phone is optional).
 *
 * @param {string|null|undefined} value - The phone number to validate
 * @returns {boolean} true if valid, false otherwise
 */
export function validatePhoneNumber(value) {
  if (value === null || value === undefined || value === "") return true;
  if (typeof value !== "string") return false;
  if (value.length > 20) return false;
  // Allow digits, parentheses, hyphens, spaces, and optional leading '+'
  const pattern = /^\+?[\d()\-\s]*$/;
  return pattern.test(value);
}

/**
 * Validates an email address.
 * Accepts string iff it contains exactly one "@" character separating
 * a non-empty local part from a non-empty domain part, AND total length <= 254.
 * Empty/null values return true (email is optional).
 *
 * @param {string|null|undefined} value - The email to validate
 * @returns {boolean} true if valid, false otherwise
 */
export function validateEmail(value) {
  if (value === null || value === undefined || value === "") return true;
  if (typeof value !== "string") return false;
  if (value.length > 254) return false;
  const parts = value.split("@");
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (local.length === 0 || domain.length === 0) return false;
  return true;
}

/**
 * Validates a piece count for equipment.
 * Accepts value iff it is a positive integer between 1 and 99 (inclusive).
 * Empty/null values return true (equipment is optional).
 *
 * @param {*} value - The piece count to validate
 * @returns {boolean} true if valid, false otherwise
 */
export function validatePieceCount(value) {
  if (value === null || value === undefined || value === "") return true;
  const num = Number(value);
  if (!Number.isInteger(num)) return false;
  if (num < 1 || num > 99) return false;
  return true;
}

/**
 * Calculates the insurance end date.
 * Takes a dayjs object, returns a new dayjs object that is exactly
 * one calendar year later at 12:00 noon.
 *
 * @param {import('dayjs').Dayjs} startDate - The start date as a dayjs object
 * @returns {import('dayjs').Dayjs} The end date (start + 1 year at 12:00 noon)
 */
export function calculateEndDate(startDate) {
  return startDate.add(1, "year").hour(12).minute(0).second(0).millisecond(0);
}

/**
 * Validates an insurance start date.
 * Accepts iff date > today + 2 days (earliest valid = today + 3 days)
 * AND date <= today + 90 days.
 *
 * @param {import('dayjs').Dayjs} date - The date to validate as a dayjs object
 * @returns {boolean} true if valid, false otherwise
 */
export function validateInsuranceStartDate(date) {
  if (!date || !dayjs.isDayjs(date)) return false;
  const today = dayjs().startOf("day");
  const earliestValid = today.add(3, "day");
  const latestValid = today.add(90, "day");
  const dateDay = date.startOf("day");
  // earliest valid <= dateDay <= latestValid
  const isAfterOrEqualEarliest =
    dateDay.isAfter(earliestValid) || dateDay.isSame(earliestValid, "day");
  const isBeforeOrEqualLatest =
    dateDay.isBefore(latestValid) || dateDay.isSame(latestValid, "day");
  return isAfterOrEqualEarliest && isBeforeOrEqualLatest;
}
